import { t, Trans } from '@lingui/macro'
import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  TextField,
  Typography
} from '@mui/material'
import { StyleSheet, Text, View } from '@react-pdf/renderer'
import { dateFormat } from 'app/appSettings'
import { FormColorAutocomplete } from 'app/views/forms/common/FormColorAutocomplete'
import moment from 'moment'
import { useDispatch } from 'react-redux'
import { myI18n } from 'translation/I18nConnectedProvider'
import {
  NumberFormatDefault
} from '../../common/Common'
import { FormConnectToObject } from '../../common/FormConnectToObject'
import {
  defaultFormPdfFontFamily,
  inuktitutFormPdfFontFamily
} from '../../pdf-components/FormPdf'
import {
  commonPDFStyles,
  pdfDefaultFontSize
} from '../../pdf-components/PDFCommon'

export const formPaymentsDefaultValue = (obj, info, item) => {
  if (!obj || !info.payments) {
    return []
  }
  return info.payments.map(payment => {
    const funding = obj.Grant_Awarded__c || 0
    let per = +((payment.FGM_Base__Amount__c / funding) * 100).toFixed(2)
    if (per === Infinity) {
      per = null
    } else if (payment.FGM_Base__Amount__c === 0) {
      per = 0
    }
    return {
      date: moment.utc(payment.FGM_Base__Scheduled_Payment_Date__c),
      percentage: per + '%',
      id: payment.Id,
      status: payment.FGM_Base__Status__c,
      report: payment.FGM_Base__Grantee_Report__c,
      amount: payment.FGM_Base__Amount__c
      // awarded: item.FGM_Base__Awarded_Amount__c
    }
  })
}

export const FormPaymentsPdf = ({
  value,
  title,
  langVersion,
  typeProps,
  ...props
}) => {
  const fontFamily =
    langVersion === 'iu-cans'
      ? inuktitutFormPdfFontFamily
      : defaultFormPdfFontFamily
  const { pdfHeaderTextProps = [], pdfBackgroundColor, pdfFontSize } = typeProps
  const headerStyle = { ...pdfStyles.headerCell }

  const pdfStyles = StyleSheet.create({
    headerCell: {
      textAlign: 'center',
      padding: 10,
      fontSize: pdfDefaultFontSize,
      flex: 1,
      fontFamily,
      border: '1px solid black',
      borderCollapse: 'collapse'
    },
    cell: {
      padding: 10,
      fontSize: pdfDefaultFontSize,
      flex: 1,
      textAlign: 'center',
      fontFamily,
      border: '1px solid black',
      borderCollapse: 'collapse'
    }
  })

  if (pdfHeaderTextProps.includes('bold')) {
    headerStyle.fontWeigth = 'bold'
  }
  if (pdfHeaderTextProps.includes('italics')) {
    headerStyle.fontStyle = 'italic'
  }
  if (pdfHeaderTextProps.includes('underline')) {
    headerStyle.textDecoration = 'underline'
  }
  if (pdfBackgroundColor) {
    headerStyle.backgroundColor = pdfBackgroundColor.id
  }
  if (pdfFontSize) {
    headerStyle.fontSize = +pdfFontSize
  }

  return (
    <View>
      {title && (
        <View>
          <Text style={commonPDFStyles.elementTitle}>{title}</Text>
        </View>
      )}
      <View style={commonPDFStyles.row}>
        <View style={headerStyle}>
          <Text>{myI18n?._(t`Date`)}</Text>
        </View>
        <View style={headerStyle}>
          <Text>{myI18n?._(t`Amount`)}</Text>
        </View>
        <View style={headerStyle}>
          <Text>{myI18n?._(t`Percentage`)}</Text>
        </View>
      </View>
      {value.map((data, index) => {
        const { date, percentage, amount } = data
        return (
          <View style={commonPDFStyles.row} key={index}>
            <View style={pdfStyles.cell}>
              <Text>{date && moment.utc(date).format(dateFormat)}</Text>
            </View>
            <View style={pdfStyles.cell}>
              <Text>
                {String(
                  langVersion !== 'fr'
                    ? new Intl.NumberFormat('en-CA', {
                      style: 'currency',
                      currency: 'CAD',
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0
                    }).format(amount)
                    : new Intl.NumberFormat('fr-CA', {
                      style: 'currency',
                      currency: 'CAD',
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0
                    })
                      .format(amount)
                      .split('CA')[0]
                )}
              </Text>
            </View>
            <View style={pdfStyles.cell}>
              <Text>{percentage}</Text>
            </View>
          </View>
        )
      })}
    </View>
  )
}

export const FormPayments = () => {
  return <div />
}

export const FormEditorPayments = ({
  depth,
  showPdfProps,
  typeProps,
  editMode,
  ...props
}) => {
  const dispatch = useDispatch()
  const { pdfHeaderTextProps = [], pdfBackgroundColor, pdfFontSize } = typeProps
  if (!editMode) {
    return <FormPayments editMode typeProps={typeProps} {...props} />
  }
  return (
    <div>
      <FormConnectToObject
        typeProps={typeProps}
        depth={depth}
        noField
        disableMultiple
      />
      {showPdfProps && (
        <div style={{ marginTop: 10 }}>
          <Typography variant='h6'>
            <Trans>Pdf props</Trans>
          </Typography>
          <div style={{ padding: 5, marginTop: 10 }}>
            <Typography style={{ marginBottom: 10, fontSize: 16 }}>
              <Trans>Header</Trans>
            </Typography>
            <TextField
              label={<Trans>Font size</Trans>}
              fullWidth
              variant='outlined'
              value={pdfFontSize || ''}
              onChange={e => {
                const toSet = { ...typeProps }
                toSet.pdfFontSize = e.target.value
                dispatch({
                  type: 'FIELD',
                  depth: depth.split('.'),
                  fieldName: 'typeProps',
                  fieldValue: toSet
                })
              }}
              inputProps={{
                maxLength: 2
              }}
              InputProps={{
                inputComponent: NumberFormatDefault
              }}
            />
            <FormGroup row>
              {[
                { key: 'bold', label: <Trans>Bold</Trans> },
                { key: 'italics', label: <Trans>Italics</Trans> },
                { key: 'underline', label: <Trans>Underline</Trans> }
              ].map(item => (
                <FormControlLabel
                  key={item.key}
                  control={
                    <Checkbox
                      checked={pdfHeaderTextProps.includes(item.key)}
                      value={item.key}
                      onChange={e => {
                        const v = e.target.value
                        const toSet = { ...typeProps }
                        const newTextProps = [...pdfHeaderTextProps]
                        if (newTextProps.includes(v)) {
                          newTextProps.splice(newTextProps.indexOf(v), 1)
                        } else {
                          newTextProps.push(v)
                        }
                        toSet.pdfHeaderTextProps = newTextProps
                        dispatch({
                          type: 'FIELD',
                          depth: depth.split('.'),
                          fieldName: 'typeProps',
                          fieldValue: { ...toSet }
                        })
                      }}
                    />
                  }
                  label={item.label}
                />
              ))}
            </FormGroup>
            <FormColorAutocomplete
              depth={depth}
              label={<Trans>Cell background color</Trans>}
              name='pdfBackgroundColor'
              typeProps={typeProps}
              value={pdfBackgroundColor}
            />
          </div>
        </div>
      )}
    </div>
  )
}
